import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Bookmark, Download, Filter, Pin, Plus, Search, Send, SlidersHorizontal, Tag as TagIcon, Trash2, X } from "lucide-react";
import { api, downloadFile, qs } from "@/lib/api";
import type { ContactFilter, ContactListResult, ContactListRow, SavedSearch } from "@/lib/types";
import { BRANDS } from "@/lib/brands";
import { displayName, formatCents, formatDate, formatInt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { LoadError } from "@/components/LoadError";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { TagPicker } from "./ContactHeader";
import { useTags } from "./hooks";
import { EMAIL_STATUSES, SORTS, describeFilter, filterOf, filterParams, isEmptyFilter, type ContactsSearch } from "./listSearch";

const statusDot: Record<string, string> = {
  ok: "var(--color-success)",
  opted_out: "var(--color-muted-foreground)",
  suppressed: "var(--color-muted-foreground)",
  unconfirmed: "var(--color-warning)",
  bounced: "var(--color-destructive)",
  complained: "var(--color-destructive)",
  invalid: "var(--color-destructive)",
};

export function useSavedSearches(withCounts = false) {
  return useQuery({
    queryKey: ["saved-searches", withCounts],
    queryFn: () => api<{ saved_searches: SavedSearch[] }>("/api/v1/saved-searches" + qs({ with_counts: withCounts || undefined })),
    staleTime: 60_000,
  });
}

export function ContactsListPage() {
  const search = useSearch({ from: "/contacts" });
  const navigate = useNavigate({ from: "/contacts" });
  const toast = useToast();
  const tags = useTags();
  const tagName = (id: number) => tags.data?.tags.find((t) => t.id === id)?.name ?? `#${id}`;
  const saved = useSavedSearches();
  const current = saved.data?.saved_searches.find((s) => s.id === search.saved);
  const filter = filterOf(search);
  const sort = search.sort ?? "newest";
  const setSearch = (next: ContactsSearch) => navigate({ search: next, replace: true });
  const setFilter = (patch: Partial<ContactFilter>) => setSearch({ ...search, ...patch });

  const [text, setText] = useState(search.q ?? "");
  useEffect(() => setText(search.q ?? ""), [search.q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? "") !== text.trim()) setFilter({ q: text.trim() || undefined });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const params = filterParams(filter);
  const q = useInfiniteQuery({
    queryKey: ["contacts", params, sort],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<ContactListResult>(
        "/api/v1/contacts" +
          qs({ ...params, sort, limit: 100, with_total: pageParam === 0, before_id: sort === "newest" && pageParam ? pageParam : undefined, offset: sort !== "newest" && pageParam ? pageParam : undefined }),
      ),
    getNextPageParam: (last) => last.next_before_id || last.next_offset || undefined,
    placeholderData: (prev) => prev,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.contacts) ?? [], [q.data]);
  const total = q.data?.pages[0]?.total;

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  useEffect(() => {
    setSelected(new Set());
    setAllMatching(false);
  }, [JSON.stringify(params), sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      setAllMatching(false);
      return n;
    });
  const allShown = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const [moreOpen, setMoreOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [bulk, setBulk] = useState<"apply" | "remove" | null>(null);
  const [tagPick, setTagPick] = useState<null | "tags_any" | "tags_all" | "tags_none">(null);

  const chips = describeFilter(filter, tagName);
  const dirty = current ? JSON.stringify(filterOf({ ...current.filters })) !== JSON.stringify(filter) : false;

  const exportCSV = async () => {
    try {
      const name = await downloadFile("/api/v1/contacts/export.csv", { filter }, "contacts.csv");
      toast(`Downloaded ${name}`);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };

  return (
    <div className="space-y-3">
      <PageHeader
        title={current ? current.name : "Contacts"}
        sub={total !== undefined ? `${formatInt(total)} ${total === 1 ? "person" : "people"}${dirty ? " (filters changed)" : ""}` : undefined}
        actions={
          <>
            <SavedSearchMenu current={current} onPick={(s) => setSearch({ ...(s.filters as ContactFilter), saved: s.id, sort: search.sort })} />
            <Button size="sm" variant="outline" onClick={() => setSaveOpen(true)} disabled={isEmptyFilter(filter) && !current}>
              <Bookmark /> {current && !dirty ? "Edit saved search" : "Save search"}
            </Button>
            <Button size="sm" variant="outline" onClick={exportCSV} title="Download everyone matching these filters as a spreadsheet (CSV)">
              <Download /> Export CSV
            </Button>
            {current && (
              <Button size="sm" variant="outline" asChild>
                <Link to="/email/segments/$segmentId" params={{ segmentId: "new" }} search={{ from_search: current.id }}>
                  <Send /> Use for an email
                </Link>
              </Button>
            )}
          </>
        }
      >
        {current?.description && <p className="text-sm text-[var(--color-muted-foreground)]">{current.description}</p>}
      </PageHeader>

      <PinnedSearches activeId={search.saved} onPick={(s) => setSearch({ ...(s.filters as ContactFilter), saved: s.id, sort: search.sort })} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
          <Input className="h-8 pl-8" value={text} onChange={(e) => setText(e.target.value)} placeholder="Name, email, phone, company…" aria-label="Filter by text" />
        </div>
        <Select className="w-auto" aria-label="Brand" value={filter.brand_id ?? ""} onChange={(e) => setFilter({ brand_id: e.target.value ? Number(e.target.value) : undefined })}>
          <option value="">All brands</option>
          {BRANDS.map((b) => (
            <option key={b.id} value={b.id}>
              {b.short}: {b.name}
            </option>
          ))}
        </Select>
        <Select className="w-auto" aria-label="Email" value={filter.email_status ?? ""} onChange={(e) => setFilter({ email_status: e.target.value || undefined })}>
          <option value="">Any email status</option>
          {EMAIL_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
        <Select className="w-auto" aria-label="Customers" value={filter.customer ?? ""} onChange={(e) => setFilter({ customer: (e.target.value || undefined) as ContactFilter["customer"] })}>
          <option value="">Customers or not</option>
          <option value="yes">Customers</option>
          <option value="no">Never bought</option>
        </Select>
        <Button size="sm" variant="outline" onClick={() => setTagPick("tags_any")}>
          <TagIcon /> Has tag…
        </Button>
        <Button size="sm" variant="outline" onClick={() => setMoreOpen(true)}>
          <SlidersHorizontal /> More filters
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Select className="w-auto" aria-label="Sort" value={sort} onChange={(e) => setSearch({ ...search, sort: e.target.value === "newest" ? undefined : e.target.value })}>
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <div role="radiogroup" aria-label="Which contacts" className="flex">
            <Button size="sm" role="radio" aria-checked={!filter.archived} className="rounded-r-none" variant={filter.archived ? "outline" : "default"} onClick={() => setFilter({ archived: undefined })}>
              Current
            </Button>
            <Button size="sm" role="radio" aria-checked={!!filter.archived} className="rounded-l-none" variant={filter.archived ? "default" : "outline"} onClick={() => setFilter({ archived: true })}>
              Archive
            </Button>
          </div>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Filter className="size-3.5 text-[var(--color-muted-foreground)]" aria-hidden />
          {chips.map((c) => (
            <Badge key={c.key} variant="outline" className="gap-1.5">
              {c.text}
              <button aria-label={`Remove filter ${c.text}`} onClick={() => setFilter({ [c.key]: undefined })} className="rounded-full hover:opacity-70">
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          <button className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => setSearch({ sort: search.sort })}>
            Clear all
          </button>
        </div>
      )}

      {(selected.size > 0 || allMatching) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-[var(--color-accent)] px-3 py-1.5 text-sm text-[var(--color-accent-foreground)]">
          <span className="num font-medium">{allMatching ? `All ${formatInt(total)} matching` : `${selected.size} selected`}</span>
          {!allMatching && allShown && total !== undefined && total > rows.length && (
            <button className="underline" onClick={() => setAllMatching(true)}>
              Select all {formatInt(total)} matching
            </button>
          )}
          <span className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setBulk("apply")}>
              <Plus /> Add tag
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulk("remove")}>
              <X /> Remove tag
            </Button>
            <Button size="sm" variant="ghost" onClick={() => (setSelected(new Set()), setAllMatching(false))}>
              Clear selection
            </Button>
          </span>
        </div>
      )}

      <Card className="overflow-x-auto">
        {q.isLoading && <Skeleton className="m-3 h-64" />}
        {q.error && <LoadError what="contacts" error={q.error} onRetry={() => (q.isFetchNextPageError ? q.fetchNextPage() : q.refetch())} />}
        {!q.isLoading && !q.error && rows.length === 0 && (
          <Empty title="No contacts match these filters.">Remove a filter above, or press ⌘K to find one person by name, email or phone.</Empty>
        )}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH className="w-8">
                  <Checkbox
                    aria-label="Select every row shown"
                    checked={allShown}
                    indeterminate={!allShown && selected.size > 0}
                    onChange={() => setSelected(allShown ? new Set() : new Set(rows.map((r) => r.id)))}
                  />
                </TH>
                <TH>ID</TH>
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Phone</TH>
                <TH>Brands</TH>
                <TH className="text-right">Tags</TH>
                <TH className="text-right">Lifetime value</TH>
                <TH>Last order</TH>
                <TH>Added</TH>
              </tr>
            </THead>
            <tbody className={cn(q.isFetching && !q.isFetchingNextPage && "opacity-60")}>
              {rows.map((r) => (
                <Row key={r.id} r={r} selected={allMatching || selected.has(r.id)} onToggle={() => toggle(r.id)} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {q.hasNextPage && (
        <Button variant="outline" size="sm" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          Show 100 more
        </Button>
      )}

      <MoreFilters open={moreOpen} onOpenChange={setMoreOpen} filter={filter} onApply={(f) => setSearch({ ...f, saved: search.saved, sort: search.sort })} tagName={tagName} />
      <TagPicker
        title="Filter by tag"
        open={tagPick !== null}
        onOpenChange={(o) => !o && setTagPick(null)}
        onPick={(id) => tagPick && setFilter({ [tagPick]: [...(filter[tagPick] ?? []).filter((x) => x !== id), id] })}
      />
      {saveOpen && <SaveSearchDialog filter={filter} current={current && !dirty ? current : current} dirty={dirty} onClose={() => setSaveOpen(false)} onSaved={(s) => setSearch({ ...(s.filters as ContactFilter), saved: s.id, sort: search.sort })} />}
      {bulk && (
        <BulkTagDialog
          mode={bulk}
          count={allMatching ? (total ?? 0) : selected.size}
          body={allMatching ? { filter } : { contact_ids: [...selected] }}
          onClose={() => setBulk(null)}
          onDone={() => {
            setSelected(new Set());
            setAllMatching(false);
          }}
        />
      )}
    </div>
  );
}

function Row({ r, selected, onToggle }: { r: ContactListRow; selected: boolean; onToggle: () => void }) {
  return (
    <TR className={cn(selected && "bg-[var(--color-row-selected)]")}>
      <TD>
        <Checkbox aria-label={`Select ${displayName(r)}`} checked={selected} onChange={onToggle} />
      </TD>
      <TD className="num text-[var(--color-muted-foreground)]">{r.id}</TD>
      <TD className="max-w-[16rem]">
        <Link to="/contacts/$contactId" params={{ contactId: String(r.id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
          {displayName({ first_name: r.first_name, last_name: r.last_name, id: r.id })}
          {r.nickname ? <span className="font-normal text-[var(--color-muted-foreground)]"> ({r.nickname})</span> : null}
        </Link>
        {r.company && <div className="truncate text-xs text-[var(--color-muted-foreground)]">{r.company}</div>}
      </TD>
      <TD className="max-w-[18rem]">
        {r.primary_email ? (
          <span className="flex items-center gap-1.5 truncate" title={r.primary_email_status}>
            <span aria-hidden className="inline-block size-1.5 shrink-0 rounded-full" style={{ background: statusDot[r.primary_email_status] ?? "var(--color-muted-foreground)" }} />
            <span className="truncate">{r.primary_email}</span>
            {r.primary_email_status !== "ok" && <span className="sr-only">({r.primary_email_status})</span>}
          </span>
        ) : (
          <span className="text-[var(--color-muted-foreground)]">—</span>
        )}
      </TD>
      <TD className="num whitespace-nowrap">{r.primary_phone || "—"}</TD>
      <TD>
        <span className="flex gap-2">
          {r.brands.map((b) => (
            <BrandMark key={b} code={b} />
          ))}
        </span>
      </TD>
      <TD className="num text-right">{r.tag_count || ""}</TD>
      <TD className="num text-right">{r.ltv_cents ? formatCents(r.ltv_cents) : ""}</TD>
      <TD className="num whitespace-nowrap">{r.last_order_at ? formatDate(r.last_order_at) : ""}</TD>
      <TD className="num whitespace-nowrap">{formatDate(r.created_at)}</TD>
    </TR>
  );
}

function PinnedSearches({ activeId, onPick }: { activeId?: number; onPick: (s: SavedSearch) => void }) {
  const saved = useSavedSearches();
  const pinned = (saved.data?.saved_searches ?? []).filter((s) => s.pinned);
  if (pinned.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Pinned saved searches">
      <Pin className="size-3.5 text-[var(--color-muted-foreground)]" aria-hidden />
      {pinned.map((s) => (
        <button
          key={s.id}
          onClick={() => onPick(s)}
          className={cn(
            "rounded-full border px-2.5 py-0.5 text-xs",
            s.id === activeId ? "border-[var(--color-primary)] bg-[var(--color-accent)] text-[var(--color-accent-foreground)]" : "hover:bg-[var(--color-muted)]",
          )}
        >
          {s.name}
        </button>
      ))}
    </div>
  );
}

function SavedSearchMenu({ current, onPick }: { current?: SavedSearch; onPick: (s: SavedSearch) => void }) {
  const [open, setOpen] = useState(false);
  const saved = useSavedSearches(open);
  const qc = useQueryClient();
  const toast = useToast();
  const del = useMutation({
    mutationFn: (id: number) => api(`/api/v1/saved-searches/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["saved-searches"] });
      toast("Saved search deleted");
    },
  });
  const list = saved.data?.saved_searches ?? [];
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline">
          <Bookmark /> Saved searches
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[60vh] w-80 overflow-y-auto">
        {list.length === 0 && <div className="px-2 py-1.5 text-sm text-[var(--color-muted-foreground)]">No saved searches yet. Filter the list, then Save search.</div>}
        {list.map((s) => (
          <DropdownMenuItem key={s.id} onSelect={() => onPick(s)} className={cn(current?.id === s.id && "bg-[var(--color-muted)]")}>
            {s.pinned && <Pin className="size-3 shrink-0" />}
            <span className="min-w-0 flex-1 truncate">{s.name}</span>
            <span className="num text-xs text-[var(--color-muted-foreground)]">{s.count !== undefined ? formatInt(s.count) : ""}</span>
            <button
              aria-label={`Delete saved search ${s.name}`}
              className="rounded p-0.5 text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (window.confirm(`Delete the saved search "${s.name}"? Contacts aren't affected.`)) del.mutate(s.id);
              }}
            >
              <Trash2 className="size-3.5" />
            </button>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SaveSearchDialog({ filter, current, dirty, onClose, onSaved }: { filter: ContactFilter; current?: SavedSearch; dirty: boolean; onClose: () => void; onSaved: (s: SavedSearch) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [asNew, setAsNew] = useState(!current);
  const [name, setName] = useState(current && !asNew ? current.name : "");
  const [description, setDescription] = useState(current?.description ?? "");
  const [pinned, setPinned] = useState(current?.pinned ?? false);
  const save = useMutation({
    mutationFn: () => api<SavedSearch>("/api/v1/saved-searches", { json: { id: asNew ? undefined : current?.id, name, description, filters: filter, pinned } }),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ["saved-searches"] });
      toast(`Saved "${s.name}"`);
      onSaved(s);
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{current && !asNew ? "Update saved search" : "Save this search"}</DialogTitle>
        <DialogDescription>Saves the filters (not the people), so it stays current. Claude and Grok can run it by name too.</DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          {current && (
            <div role="radiogroup" className="flex gap-1">
              <Button type="button" size="sm" role="radio" aria-checked={!asNew} variant={!asNew ? "default" : "outline"} onClick={() => (setAsNew(false), setName(current.name))}>
                Update "{current.name}"{dirty ? " with these filters" : ""}
              </Button>
              <Button type="button" size="sm" role="radio" aria-checked={asNew} variant={asNew ? "default" : "outline"} onClick={() => (setAsNew(true), setName(""))}>
                Save as new
              </Button>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="ss-name">Name</Label>
            <Input id="ss-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} required placeholder="MHU customers, last 12 months" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ss-desc">What it's for (optional)</Label>
            <Textarea id="ss-desc" className="min-h-16" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={pinned} onChange={(e) => setPinned(e.target.checked)} /> Pin it above the list
          </label>
          {save.error && <p className="text-sm text-[var(--color-destructive)]">{save.error.message}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              Save search
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkTagDialog({ mode, count, body, onClose, onDone }: { mode: "apply" | "remove"; count: number; body: Record<string, unknown>; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pick, setPick] = useState(true);
  const [tag, setTag] = useState<{ id: number; name: string } | null>(null);
  const [note, setNote] = useState("");
  const run = useMutation({
    mutationFn: () =>
      api<{ matched: number; changed: number; truncated: boolean }>(`/api/v1/tags/${mode === "apply" ? "apply-many" : "remove-many"}`, {
        json: { ...body, tag_ids: [tag!.id], source_ref: note || undefined },
      }),
    onSuccess: (r) => {
      toast(
        `${mode === "apply" ? "Added" : "Removed"} ${tag!.name} ${mode === "apply" ? "to" : "from"} ${formatInt(r.changed)} ${r.changed === 1 ? "person" : "people"}${r.matched > r.changed ? ` (${formatInt(r.matched - r.changed)} already ${mode === "apply" ? "had it" : "didn't have it"})` : ""}${r.truncated ? ". Stopped at 10,000: run it again with a narrower filter for the rest." : ""}`,
      );
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      onDone();
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  if (pick && !tag) {
    return (
      <TagPicker
        title={mode === "apply" ? "Tag to add" : "Tag to remove"}
        open
        onOpenChange={(o) => !o && (tag ? setPick(false) : onClose())}
        onPick={(id, name) => {
          setTag({ id, name });
          setPick(false);
        }}
      />
    );
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>
          {mode === "apply" ? "Add" : "Remove"} “{tag?.name}” {mode === "apply" ? "to" : "from"} {formatInt(count)} {count === 1 ? "person" : "people"}?
        </DialogTitle>
        <DialogDescription>
          Each change goes into the person's tag history and timeline. {mode === "apply" ? "Adding a tag can start automations for these people." : "Removing a tag can stop automations that wait for it."}
          {count > 10000 && " Only the first 10,000 are done per run."}
        </DialogDescription>
        <div className="space-y-1">
          <Label htmlFor="bulk-note">Note for the tag history (optional)</Label>
          <Input id="bulk-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Boot Camp follow-up list" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={mode === "remove" ? "destructive" : "default"} disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? "Working…" : `${mode === "apply" ? "Add" : "Remove"} tag`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MoreFilters({
  open,
  onOpenChange,
  filter,
  onApply,
  tagName,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  filter: ContactFilter;
  onApply: (f: ContactFilter) => void;
  tagName: (id: number) => string;
}) {
  const [f, setF] = useState<ContactFilter>(filter);
  const [pick, setPick] = useState<null | "tags_any" | "tags_all" | "tags_none">(null);
  useEffect(() => {
    if (open) setF(filter);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: Partial<ContactFilter>) => setF((x) => ({ ...x, ...patch }));
  const dollars = (cents?: number) => (cents ? String(cents / 100) : "");
  const tagList = (key: "tags_any" | "tags_all" | "tags_none", label: string, help: string) => (
    <div className="space-y-1">
      <Label>{label}</Label>
      <p className="text-xs text-[var(--color-muted-foreground)]">{help}</p>
      <div className="flex flex-wrap gap-1.5">
        {(f[key] ?? []).map((id) => (
          <Badge key={id} variant="default">
            {tagName(id)}
            <button aria-label={`Remove ${tagName(id)}`} onClick={() => set({ [key]: (f[key] ?? []).filter((x) => x !== id) })}>
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        <button className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]" onClick={() => setPick(key)}>
          <Plus className="size-3" /> tag
        </button>
      </div>
    </div>
  );
  const text = (key: keyof ContactFilter, label: string, type = "text", placeholder = "") => (
    <div className="space-y-1">
      <Label htmlFor={`mf-${key}`}>{label}</Label>
      <Input id={`mf-${key}`} type={type} className="h-8" value={(f[key] as string) ?? ""} placeholder={placeholder} onChange={(e) => set({ [key]: e.target.value || undefined })} />
    </div>
  );
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent title="More filters" description="Everything here combines with AND. Dates are Mountain Time and include the day you pick.">
        <div className="space-y-4 p-4">
          {tagList("tags_any", "Has any of these tags", "People with at least one of them.")}
          {tagList("tags_all", "Has all of these tags", "People with every one of them.")}
          {tagList("tags_none", "Has none of these tags", "Leaves out anyone with one of them, e.g. Opt-Out.")}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!f.subscribed} onChange={(e) => set({ subscribed: e.target.checked || undefined })} />
            Subscribed to marketing{f.brand_id ? " in the chosen brand" : " in any brand"}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="mf-ltvmin">Lifetime value at least ($)</Label>
              <Input id="mf-ltvmin" type="number" min={0} className="h-8" value={dollars(f.ltv_min_cents)} onChange={(e) => set({ ltv_min_cents: e.target.value ? Math.round(Number(e.target.value) * 100) : undefined })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mf-ltvmax">Lifetime value at most ($)</Label>
              <Input id="mf-ltvmax" type="number" min={0} className="h-8" value={dollars(f.ltv_max_cents)} onChange={(e) => set({ ltv_max_cents: e.target.value ? Math.round(Number(e.target.value) * 100) : undefined })} />
            </div>
            {text("created_since", "Added on or after", "date")}
            {text("created_until", "Added on or before", "date")}
            {text("last_order_since", "Last order on or after", "date")}
            {text("last_order_until", "Last order on or before", "date")}
            {text("lead_source", "Lead source", "text", "Webinar")}
            {text("access", "Has access", "text", "club")}
            {text("utm_source", "UTM source (first or last touch)", "text", "facebook")}
            {text("utm_campaign", "UTM campaign", "text", "bootcamp-2026")}
            <div className="space-y-1">
              <Label htmlFor="mf-aff">Referred by affiliate ID</Label>
              <Input id="mf-aff" inputMode="numeric" className="h-8" value={f.affiliate_id ?? ""} onChange={(e) => set({ affiliate_id: Number(e.target.value.replace(/\D/g, "")) || undefined })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={() => setF({ archived: filter.archived })}>
              Clear these
            </Button>
            <Button
              onClick={() => {
                onApply(f);
                onOpenChange(false);
              }}
            >
              Show matching contacts
            </Button>
          </div>
        </div>
        <TagPicker title="Pick a tag" open={pick !== null} onOpenChange={(o) => !o && setPick(null)} onPick={(id) => pick && set({ [pick]: [...(f[pick] ?? []).filter((x) => x !== id), id] })} />
      </SheetContent>
    </Sheet>
  );
}

